import { checkSession } from "@features/auth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { Checkbox } from "@shadcn/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shadcn/ui/collapsible";
import { Separator } from "@shadcn/ui/separator";
import { createFileRoute, Navigate, redirect, useNavigate } from "@tanstack/react-router";
import { ChevronDown, Container, CreditCard, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { AppHeader } from "~/components/layout/AppHeader";
import { BackLink } from "~/components/one-delivery/BackLink";
import { DeliveryWeekdaysHeader, DeliveryWeekdaysPicker } from "~/components/one-delivery/DeliveryWeekdays";
import { PageContainer } from "~/components/layout/PageContainer";
import { PayPerUsePlanCard } from "~/components/one-delivery/PayPerUsePlanCard";
import type { summaryOneDeliveryOnboardingCompleteMutation } from "./__generated__/summaryOneDeliveryOnboardingCompleteMutation.graphql";
import type { summaryOneDeliveryOnboardingQuery } from "./__generated__/summaryOneDeliveryOnboardingQuery.graphql";

const query = graphql`
	query summaryOneDeliveryOnboardingQuery {
		onedeliveryProfile {
			baceId
			deliveryWeekdays
			preferredHub {
				id
				meta {
					id
					name
					street
					postalCode
					city
				}
			}
		}
	}
`;

const completeMutation = graphql`
	mutation summaryOneDeliveryOnboardingCompleteMutation {
		onedeliveryOnboardingComplete {
			userId
			onboardedAt
		}
	}
`;

export const Route = createFileRoute("/one-delivery/onboarding/summary")({
	beforeLoad: () => {
		document.title = "bacehub | One Delivery";
	},
	component: RouteComponent,
	loader: async ({ context }) => {
		if (!(await checkSession())) {
			throw redirect({ to: "/auth", search: { redirectToPath: "/one-delivery/onboarding/pricing", show: "sign-in" } });
		}
		return preloadQuery<summaryOneDeliveryOnboardingQuery>(context.relayEnvironment, query, {}, { fetchPolicy: "network-only" });
	},
});

/** Figma "OD - Order Summary": the order is paid through the Credits page. */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { onedeliveryProfile: profile } = usePreloadedQuery<summaryOneDeliveryOnboardingQuery>(query, Route.useLoaderData());
	const [consent, setConsent] = useState(false);
	const [complete, isCompleting] = useAsyncMutation<summaryOneDeliveryOnboardingCompleteMutation>(completeMutation);
	// Accepting the terms completes the onboarding; the Credits are bought next.
	const order = async () => {
		try {
			await complete({ variables: {} });
			await navigate({ to: "/one-delivery/credits", search: { onboarding: true } });
		} catch (error) {
			toast.error(i18n.OD_ONB_COMPLETE_FAILED(), { description: error instanceof Error ? error.message : undefined });
		}
	};
	if (!profile) return <Navigate to="/one-delivery/onboarding/pricing" replace />;
	const hub = profile.preferredHub.meta;
	const terms = [
		[i18n.OD_ONB_TERM_DURATION(), i18n.OD_ONB_TERM_DURATION_TEXT()],
		[i18n.OD_ONB_TERM_BUNDLES(), i18n.OD_ONB_TERM_BUNDLES_TEXT()],
		[i18n.OD_ONB_TERM_PAUSE(), i18n.OD_ONB_TERM_PAUSE_TEXT()],
		[i18n.OD_ONB_TERM_STORAGE(), i18n.OD_ONB_TERM_STORAGE_TEXT()],
		[i18n.OD_ONB_TERM_OVERSIZE(), i18n.OD_ONB_TERM_OVERSIZE_TEXT()],
	] as const;

	return (
		<PageContainer className="min-h-dvh gap-0 bg-slate-50">
			<AppHeader />
			<BackLink to="/one-delivery/onboarding/pricing" />
			<div className="flex flex-col gap-4 pt-8">
				<h1 className="whitespace-pre-line px-6 font-breit text-2xl font-normal text-neutral-900">
					<T>{i18n.OD_ONB_ORDER_TITLE()}</T>
				</h1>
				<Separator className="mx-6 w-auto!" />
				<p className="px-6 font-breit text-sm font-medium text-neutral-950">{i18n.OD_ONB_SELECTED_PLAN()}</p>
				<div className="px-6">
					<PayPerUsePlanCard />
				</div>
				<Separator className="mx-6 w-auto!" />
				<p className="px-6 font-breit text-sm font-medium text-neutral-950">{i18n.OD_ONB_SELECTED_HUB()}</p>
				<div className="flex gap-4 px-6 py-5">
					<Container className="size-6 shrink-0" />
					<div className="flex min-w-0 flex-col">
						<p className="font-breit text-base leading-none font-medium text-neutral-950">
							{i18n.OD_ONB_HUB_NAME({ name: hub?.name ?? "" })}
						</p>
						{hub && (
							<p className="pt-1 text-sm text-neutral-500">
								{hub.street},
								<br />
								{hub.postalCode} {hub.city}
							</p>
						)}
					</div>
				</div>
				<Separator className="mx-6 w-auto!" />
				<div className="px-6 pb-6">
					<DeliveryWeekdaysHeader title={i18n.OD_WEEKDAYS_SELECT()} />
					<DeliveryWeekdaysPicker initial={profile.deliveryWeekdays} />
				</div>
				<div className="flex flex-col gap-4 bg-zinc-100 px-6 pt-4">
					<p className="font-breit text-sm font-medium text-neutral-950">{i18n.OD_ONB_TERMS_TITLE()}</p>
					<div className="flex flex-col">
						{terms.map(([title, text]) => (
							<Collapsible key={title} className="group border-b border-neutral-200">
								<CollapsibleTrigger className="flex w-full items-center justify-between py-4 text-left text-sm font-medium text-neutral-950">
									{title}
									<ChevronDown className="size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
								</CollapsibleTrigger>
								<CollapsibleContent className="pb-4 text-sm text-neutral-500">{text}</CollapsibleContent>
							</Collapsible>
						))}
					</div>
					<p className="font-breit text-sm font-medium text-neutral-950">{i18n.OD_ONB_CONSENT_TITLE()}</p>
					<label className="flex gap-2">
						<Checkbox className="mt-0.5 bg-white" checked={consent} onCheckedChange={(checked) => setConsent(checked === true)} />
						<span className="flex flex-col gap-1.5">
							<span className="text-sm font-medium text-neutral-950">{i18n.OD_ONB_CONSENT_LABEL()}</span>
							<span className="text-sm text-neutral-500">{i18n.OD_ONB_CONSENT_TEXT()}</span>
						</span>
					</label>
					<div className="py-6">
						<Button
							size="lg"
							className="w-full font-breit font-normal shadow-xl"
							disabled={!consent || isCompleting}
							onClick={() => void order()}
						>
							{isCompleting && <LoaderCircle className="size-4 animate-spin" />}
							{i18n.OD_ONB_ORDER()}
							<CreditCard className="size-4" />
						</Button>
					</div>
				</div>
			</div>
		</PageContainer>
	);
}
